import React from 'react';
import { Modal, Button, Table, Form } from 'react-bootstrap';
import printing, { testPrint } from '../services/printing';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';

const SaleConfirmationModal = ({
  show,
  onHide,
  onConfirm,
  onConfirmPrint,
  cart,
  customer,
  customerType,
  deliveryPrice,
  paymentType,
  hybridPayments = [],
  business,
  loading = false,
  onBooking = null,
  onBookingSuccess = null
}) => {
  const [isPrinting, setIsPrinting] = React.useState(false);
  const [showNoteModal, setShowNoteModal] = React.useState(false);
  const [note, setNote] = React.useState('');
  const [isBooking, setIsBooking] = React.useState(false);
  const { token } = useAuth();
  const totalSum = cart.reduce((sum, item) => sum + (item.qty * item.price), 0);
  const overal = totalSum + Number(deliveryPrice);

  const isWorking = loading || isPrinting || isBooking;

  const handleBooking = async () => {
    if (!onBooking) {
      // If no onBooking handler, create booking directly
      setShowNoteModal(true);
    } else {
      // Use provided handler
      onBooking();
    }
  };

  const handleConfirmBooking = async () => {
    setIsBooking(true);
    try {
      // Prepare booking items from cart
      const items = cart.map(item => ({
        product_id: item.id || item.productid,
        purchase_batch_id: item.batch_id,
        quantity: item.qty,
        price: item.price,
        barcode: item.batch_barcode || null // Include barcode for specific products
      }));

      const response = await fetch('/api/bookings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          items,
          delivery_price: deliveryPrice || 0,
          note: note.trim() || null
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Booking failed');
      }

      toast.success('Bron qilindi');
      setShowNoteModal(false);
      setNote('');
      if (onBookingSuccess) onBookingSuccess();
      if (onHide) onHide();
    } catch (error) {
      console.error('Booking error:', error);
      toast.error(error.message || 'Bron qilishda xatolik');
    } finally {
      setIsBooking(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered backdrop={isWorking ? 'static' : true}>
      <Modal.Header closeButton={!isWorking}>
        <Modal.Title>Sotuvni tasdiqlash</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <h5>Mijoz ma'lumotlari</h5>
        <div><b>Turi:</b> {customerType === 'customer1' ? '1-turi' : customerType === 'customer2' ? '2-turi (qarzga)' : customerType === 'customer3' ? '3-turi' : '-'}</div>
        <div><b>Ism:</b> {customer?.name || '-'}</div>
        <div><b>Telefon:</b> {customer?.phone || '-'}</div>
        <div>
          <b>To'lov turi:</b>{' '}
          {paymentType === 'hybrid' ? (
            <>
              Gibrid
              {Array.isArray(hybridPayments) && hybridPayments.length > 0 && (
                <div className="mt-2">
                  <Table bordered size="sm" className="mb-0">
                    <thead>
                      <tr>
                        <th>Usul</th>
                        <th>Miqdor</th>
                        <th>Valyuta</th>
                      </tr>
                    </thead>
                    <tbody>
                      {hybridPayments.map((p, i) => (
                        <tr key={i}>
                          <td>{p.method === 'cash' ? 'Naqd' : p.method === 'terminal' ? 'Terminal' : p.method === 'card' ? 'Karta' : p.method}</td>
                          <td>{Number(p.amount || 0).toLocaleString()}</td>
                          <td>{p.currency || 'UZS'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </>
          ) : (
            paymentType === 'cash' ? 'Naqd' : paymentType === 'card' ? 'Karta' : paymentType === 'terminal' ? 'Terminal' : paymentType
          )}
        </div>
        <hr />
        <h5>Savat</h5>
        <Table bordered size="sm">
          <thead>
            <tr>
              <th>#</th>
              <th>Nom</th>
              <th>Narx</th>
              <th>Miqdor</th>
              <th>Summa</th>
            </tr>
          </thead>
          <tbody>
            {cart.map((item, idx) => (
              <tr key={item.id + (item.batch_id || '')}>
                <td>{idx + 1}</td>
                <td>{item.name}{item.batch_barcode ? ` (${item.batch_barcode})` : ''}</td>
                <td>{item.price}</td>
                <td>{item.qty}</td>
                <td>{((item.qty * item.price) || 0).toLocaleString()} UZS</td>
              </tr>
            ))}
          </tbody>
        </Table>
        <div className="d-flex justify-content-between align-items-center mt-3">
          <div><b>Jami:</b> {totalSum.toLocaleString()} UZS</div>
          {deliveryPrice > 0 && (
            <div>+ {Number(deliveryPrice).toLocaleString()} UZS (Yetkazib berish)</div>
          )}
          <div style={{ fontWeight: 600, fontSize: 18 }}>Umumiy: {overal.toLocaleString()} UZS</div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isWorking}>Bekor qilish</Button>
        <Button
          variant="info"
          className="text-white"
          disabled={isWorking}
          onClick={async () => {
            setIsPrinting(true);
            try {
              await printing.connectPrinter();
              await printing.printSaleReceipt({
                business: business || { name: 'Kassa', address: '', phone: '' },
                sale: { sellBatchID: 'DRAFT-' + Date.now() }, // Mark as draft since it's not submitted
                cart,
                payments: paymentType === 'hybrid' ? hybridPayments : [{ method: paymentType, amount: overal, currency: 'UZS' }],
                deliveryPrice: Number(deliveryPrice) || 0,
                totals: { totalSum, overall: overal },
                options: { width: 32, type: 'epson' }
              });
            } catch (e) {
              console.error('Print error:', e);
              alert('Chop etishda xatolik: ' + (e?.message || e));
            } finally {
              setIsPrinting(false);
            }
          }}
        >
          {isPrinting ? 'Chop etilmoqda...' : 'Shunchaki chop etish'}
        </Button>
        <Button
          variant="primary"
          disabled={isWorking}
          onClick={() => {
            // Validation: Ensure payment method is selected for Guest/Customer1/Customer3 (Customer2 is handled separately)
            if (!paymentType && customerType !== 'customer2') {
              toast.error("To'lov usulini tanlang");
              return;
            }
            onConfirm();
          }}
        >
          {loading ? 'Yuborilmoqda...' : 'Tasdiqlash'}
        </Button>
        <Button
          variant="success"
          disabled={isWorking}
          onClick={async () => {
            // Validation: Ensure payment method is selected for Guest/Customer1/Customer3 (Customer2 is handled separately)
            if (!paymentType && customerType !== 'customer2') {
              toast.error("To'lov usulini tanlang");
              return;
            }

            if (typeof onConfirmPrint === 'function') {
              onConfirmPrint();
            } else if (typeof onConfirm === 'function') {
              onConfirm();
            }
          }}
        >
          {loading ? 'Yuborilmoqda...' : 'Sotish va chop etish'}
        </Button>
        <Button
          variant="warning"
          disabled={isWorking}
          onClick={handleBooking}
        >
          {isBooking ? 'Bron qilinmoqda...' : 'Bron qilish'}
        </Button>
      </Modal.Footer>

      {/* Note Modal for Booking */}
      <Modal show={showNoteModal} onHide={() => !isBooking && setShowNoteModal(false)} centered>
        <Modal.Header closeButton={!isBooking}>
          <Modal.Title>Bron qilish</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Izoh (ixtiyoriy)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Bron uchun izoh kiriting..."
                disabled={isBooking}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowNoteModal(false)} disabled={isBooking}>
            Bekor qilish
          </Button>
          <Button variant="warning" onClick={handleConfirmBooking} disabled={isBooking}>
            {isBooking ? 'Bron qilinmoqda...' : 'Tasdiqlash'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Modal >
  );
};

export default SaleConfirmationModal; 